'use client'

/**
 * Root Error Boundary
 *
 * Catches errors in the app and displays a friendly recovery UI.
 * Reports full error details to console for debugging.
 */

import { useEffect } from 'react'
import { WarningCircleIcon, ArrowClockwiseIcon } from '@phosphor-icons/react'

type ErrorProps = {
  error: Error & { digest?: string }
  reset: () => void
}

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Log detailed error to console for debugging
    console.error('Application error:', {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      name: error.name,
    })

    // Also log to any error reporting service if configured
    if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
      // Example: Sentry.captureException(error)
    }
  }, [error])

  return (
    <div className="bg-background fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6 text-center">
        {/* Error Icon */}
        <div className="flex justify-center">
          <div className="bg-destructive/10 rounded-full p-6">
            <WarningCircleIcon
              size={48}
              weight="duotone"
              className="text-destructive"
            />
          </div>
        </div>

        {/* Error Title */}
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">
            Something went wrong
          </h1>
          <p className="text-muted-foreground text-sm">
            The application encountered an unexpected error. We&apos;ve logged
            the details for investigation.
          </p>
        </div>

        {/* Error Details (for debugging) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="border-destructive/20 bg-destructive/5 rounded-lg border p-4 text-left">
            <div className="text-destructive mb-2 text-xs font-medium">
              Error Details (dev only):
            </div>
            <pre className="text-muted-foreground overflow-x-auto text-xs">
              {error.message}
            </pre>
            {error.digest && (
              <div className="text-muted-foreground mt-2 text-xs">
                Digest: {error.digest}
              </div>
            )}
          </div>
        )}

        {/* Show error message in production too */}
        {process.env.NODE_ENV === 'production' && (
          <div className="border-destructive/20 bg-destructive/5 rounded-lg border p-4">
            <div className="text-destructive text-xs font-medium">
              Error: {error.message}
            </div>
            {error.digest && (
              <div className="text-muted-foreground mt-1 text-xs">
                Error ID: {error.digest}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            onClick={reset}
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center gap-2 rounded-md px-6 py-2.5 text-sm font-medium transition-colors"
          >
            <ArrowClockwiseIcon size={16} weight="bold" />
            Try again
          </button>

          <button
            onClick={() => {
              // Clear all local storage and reload
              if (
                confirm(
                  'This will clear all local data and reload the app. Continue?',
                )
              ) {
                localStorage.clear()
                sessionStorage.clear()
                window.location.href = '/'
              }
            }}
            className="border-border bg-background hover:bg-accent inline-flex items-center justify-center gap-2 rounded-md border px-6 py-2.5 text-sm font-medium transition-colors"
          >
            Reset app
          </button>
        </div>

        {/* Additional Help */}
        <div className="text-muted-foreground text-xs">
          If this problem persists, try clearing your browser cache or contact
          support.
        </div>
      </div>
    </div>
  )
}
